import React, { FunctionComponent, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { List, Paper } from "@mui/material";
import _ from "underscore";
import { PipelineName } from "avvir";

import "../../css/running_processes_overlay.css";
import checkPipelineStatus from "../actions/check_pipeline_status";
import runPipeline from "../actions/run_pipeline";
import toOrganizationSupportCenterPage from "../events/routing/to_organization_support_center_page";
import toSupportCenterPage from "../events/routing/to_support_center_page";
import { useFlags } from "../services/utilities/launch_darkly_helper";
import getOrganizationId from "../services/getters/organization_getters/get_organization_id";
import getPipelineRunningProcessesById from "../services/getters/running_processes_getters/get_pipeline_running_processes_by_id";
import getProjectIdFromLocation from "../services/getters/location_metadata/get_project_id_from_location";
import getRunningExportProcesses from "../services/getters/running_processes_getters/get_running_export_processes";
import getUserAccount from "../services/getters/user_getters/get_user_account";
import RunningProcessOverlayItem from "./running_process_overlay_item";
import { isProcessWithId } from "../services/utilities/custom_type_guards";
import { readDismissedPipelineIds, readPipelines, writeDismissedPipelineId } from "../services/utilities/store_in_local_storage";

import type Pipeline from "../models/domain/pipeline";
import type { Dispatch } from "type_aliases";
import type { ProcessTypes } from "../models/domain/process";
import { BatchIfcType } from "./dialogs/batch_ifc_floor_selection_dialog";

export type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>

export type PipelineProcess = (Pipeline | ProcessTypes) & { key?: string, ifcType?: string };

export const RunningProcessesOverlay: FunctionComponent<Props> = function RunniingProcessesOverlay(props) {
  const { exportProcesses, pipelinesById, userAccount, checkPipeline, retryExport, projectId, organizationId, navigateToSupportCenter, navigateToOrganizationSupportCenter } = props;
  const { ifcDownloadImprovements: ifcDownloadImprovementsFlag, supportCenterEnabled } = useFlags();

  const onContactSupport = supportCenterEnabled && (projectId || organizationId)
    ? () => (projectId ? navigateToSupportCenter(projectId) : navigateToOrganizationSupportCenter(organizationId))
    : undefined;
  const [pipelines, setPipelines] = useState<PipelineProcess[]>([]);
  const [terminatedPipelines, setTerminatedPipelines] = useState([]);
  const email = userAccount == null ? null : userAccount.email;
  const [dismissedPipelineIds, setDismissedPipelineIds] = useState({});

  const updateDismissedPipelineIds = useCallback(() => {
    setDismissedPipelineIds(readDismissedPipelineIds());
  }, []);

  useEffect(updateDismissedPipelineIds, [updateDismissedPipelineIds]);

  useEffect(() => {
    if (email != null) {
      const stored = readPipelines();
      stored.forEach((pipeline) => checkPipeline(pipeline));
    }
  }, [checkPipeline, email]);

  useEffect(() => {
    //set up the pipelines as a normalized array
    const newPipelines: PipelineProcess[] = [...exportProcesses.map(process => {
      if (isProcessWithId(process)) {
        return {
          ...process,
          key: `${process.processId}`
        };
      } else {
        return {
          ...process,
          key: process.processName,
        };
      }
    })];
    const [runningPipelines, terminatedPipelines] = _.partition(_(pipelinesById).values(), (pipeline) => {
      return pipeline.status === "RUNNING";
    });

    newPipelines.push(...runningPipelines.map(pipeline => ({
      ...pipeline,
      key: `pipeline_process_${pipeline.id}`,
      name: pipeline.name.split("-").join(" ") as PipelineName
    })));

    const tenMinutesAgo = new Date(new Date().getTime() - 10 * 60 * 1000);
    const newTerminated = terminatedPipelines.map(pipeline => ({
        ...pipeline,
        key: `pipeline_process_${pipeline.id}`,
        name: pipeline.name.split("-").join(" ") as PipelineName
      }))
      .filter(pipeline => {
        // Key off the most recent of end/start so recently-terminated toasts show but stale ones don't.
        const terminatedAt = pipeline.endDate ?? pipeline.startDate;
        return terminatedAt != null && terminatedAt > tenMinutesAgo;
      });
    setPipelines(newPipelines);
    setTerminatedPipelines(newTerminated);
  }, [pipelinesById, exportProcesses]);

  if (terminatedPipelines.length === 0 && pipelines.length === 0) {
    return null;
  }

  const items = <>
    {pipelines
      .filter((pipeline: PipelineProcess) => {
        if (typeof (pipeline as any).id === "undefined") {
          return true;
        } else {
          return !dismissedPipelineIds[(pipeline as any).id];
        }
      })
      .map((pipeline: PipelineProcess) => {
        return <RunningProcessOverlayItem
          key={pipeline.key}
          startTime={pipeline.startDate}
          endTime={pipeline?.endDate || new Date()}
          title={pipeline.name}
          status={pipeline.status}
          onContactSupport={onContactSupport}
          onDismiss={() => {
            if (typeof (pipeline as any).id !== "undefined") {
              writeDismissedPipelineId((pipeline as any)?.id);
              updateDismissedPipelineIds();
            }
          }}
        />;
      })}
    {terminatedPipelines.map((pipeline: PipelineProcess) => {
      return <RunningProcessOverlayItem
        key={pipeline.key}
        startTime={pipeline.startDate}
        endTime={pipeline?.endDate || new Date()}
        title={pipeline.name}
        status={pipeline.status}
        downloadUrl={(pipeline as any).downloadUrl}
        downloadFileName={(pipeline as any).downloadFileName}
        onRetry={(pipeline as any).retryArguments ? () => retryExport((pipeline as any).retryArguments) : undefined}
        onContactSupport={onContactSupport}
        onDismiss={() => {}}
      />;
    })}
  </>;

  // Nova toast stack behind the flag; the legacy Paper/List panel until release.
  return ifcDownloadImprovementsFlag ?
         <div className="RunningProcessesOverlay RunningProcessesOverlay--nova">
           {items}
         </div> :
         <Paper className="RunningProcessesOverlay">
           <List dense disablePadding>
             {items}
           </List>
         </Paper>;
};

const mapStateToProps = (state, props) => ({
  userAccount: getUserAccount(state, props),
  pipelinesById: getPipelineRunningProcessesById(state, props),
  exportProcesses: getRunningExportProcesses(state, props),
  projectId: getProjectIdFromLocation(state, props),
  organizationId: getOrganizationId(state, props)
});

const mapDispatchToProps = (dispatch: Dispatch<any>) => bindActionCreators({
  checkPipeline: checkPipelineStatus,
  retryExport: runPipeline,
  navigateToSupportCenter: toSupportCenterPage,
  navigateToOrganizationSupportCenter: toOrganizationSupportCenterPage
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(RunningProcessesOverlay);
